import { Accordion as ArkAccordion } from '@ark-ui/react/accordion';
import { css, cva } from '@styled-system/css';
import { ChevronDown } from 'lucide-react';
import type { FC, ReactNode } from 'react';

interface AccordionRootProps {
  /** Allow multiple items to be open at once. */
  multiple?: boolean;
  /** Initially open item value(s). For multiple={false}, pass a single string. */
  defaultValue?: string[];
  /** Controlled open value(s). */
  value?: string[];
  onValueChange?: (next: string[]) => void;
  children: ReactNode;
  className?: string;
}

const root = css({
  display: 'flex',
  flexDirection: 'column',
  width: 'full',
  gap: '3',
});

const item = css({
  bg: 'surface.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.subtle',
  borderRadius: 'lg',
  overflow: 'hidden',
  transition: 'border-color 160ms ease, box-shadow 160ms ease',
  _hover: { borderColor: 'border.default' },
  '&[data-state="open"]': {
    borderColor: 'border.default',
    boxShadow: 'xs',
  },
});

const trigger = css({
  display: 'flex',
  alignItems: 'center',
  width: 'full',
  paddingY: '5',
  paddingX: '6',
  bg: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'sans',
  textAlign: 'left',
  transition: 'background 120ms ease',
  _hover: { bg: 'surface.subtle' },
});

const headerInner = css({
  display: 'flex',
  alignItems: 'center',
  gap: '5',
  flex: 1,
  minWidth: 0,
});

const headerIcon = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  color: 'text.secondary',
  flexShrink: 0,
});

const headerLabel = css({
  fontSize: 'body',
  fontWeight: 'medium',
  color: 'text.primary',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flex: 1,
});

const countBadge = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '24px',
    height: '24px',
    paddingX: '3',
    borderRadius: 'full',
    fontSize: 'bodySm',
    fontWeight: 'semibold',
    fontFamily: 'mono',
    flexShrink: 0,
  },
  variants: {
    tone: {
      neutral: { bg: 'surface.sunken', color: 'text.secondary' },
      brand:   { bg: 'brand.primarySoft', color: 'text.brand' },
    },
  },
  defaultVariants: { tone: 'brand' },
});

const indicator = css({
  display: 'inline-flex',
  alignItems: 'center',
  color: 'text.tertiary',
  flexShrink: 0,
  transition: 'transform 200ms ease',
  '&[data-state="open"]': { transform: 'rotate(180deg)' },
});

const content = css({
  paddingTop: '0',
  paddingBottom: '6',
  paddingX: '6',
  bg: 'surface.default',
  '&[data-state="closed"]': { display: 'none' },
});

interface AccordionItemProps {
  value: string;
  children: ReactNode;
  className?: string;
}

interface AccordionHeaderProps {
  icon?: ReactNode;
  label: ReactNode;
  count?: number;
  className?: string;
}

interface AccordionContentProps {
  children: ReactNode;
  className?: string;
}

const AccordionItem: FC<AccordionItemProps> = ({ value, children, className }) => (
  <ArkAccordion.Item value={value} className={`${item} ${className ?? ''}`}>
    {children}
  </ArkAccordion.Item>
);

const AccordionHeader: FC<AccordionHeaderProps> = ({ icon, label, count, className }) => (
  <ArkAccordion.ItemTrigger className={`${trigger} ${className ?? ''}`}>
    <div className={headerInner}>
      {icon && <span className={headerIcon}>{icon}</span>}
      <span className={headerLabel}>{label}</span>
      {count != null && count > 0 && (
        <span className={countBadge({ tone: 'brand' })}>{count}</span>
      )}
    </div>
    <ArkAccordion.ItemIndicator className={indicator}>
      <ChevronDown size={16} strokeWidth={1.75} />
    </ArkAccordion.ItemIndicator>
  </ArkAccordion.ItemTrigger>
);

const AccordionContent: FC<AccordionContentProps> = ({ children, className }) => (
  <ArkAccordion.ItemContent className={`${content} ${className ?? ''}`}>
    {children}
  </ArkAccordion.ItemContent>
);

interface AccordionComponent extends FC<AccordionRootProps> {
  Item: typeof AccordionItem;
  Header: typeof AccordionHeader;
  Content: typeof AccordionContent;
}

const AccordionRoot: FC<AccordionRootProps> = ({
  multiple = true,
  defaultValue,
  value,
  onValueChange,
  children,
  className,
}) => (
  <ArkAccordion.Root
    multiple={multiple}
    defaultValue={defaultValue}
    value={value}
    onValueChange={onValueChange ? (details) => onValueChange(details.value) : undefined}
    className={`${root} ${className ?? ''}`}
  >
    {children}
  </ArkAccordion.Root>
);

export const Accordion = AccordionRoot as AccordionComponent;
Accordion.Item = AccordionItem;
Accordion.Header = AccordionHeader;
Accordion.Content = AccordionContent;
